import { cn } from '../../lib/utils';

interface InitialsAvatarProps {
	name: string;
	/** Image URL — if provided, renders an image instead of initials */
	src?: string | null;
	size?: number;
	/** Number of initials to show (default 2) */
	maxInitials?: number;
	/** Border radius style — "round" for circles, "rounded" for rounded squares */
	shape?: 'round' | 'rounded';
	className?: string;
}

/**
 * Renders a user/org avatar with initials fallback.
 * If `src` is provided, renders an image. Otherwise shows initials derived from `name`.
 */
export function InitialsAvatar({
	name,
	src,
	size = 32,
	maxInitials = 2,
	shape = 'round',
	className,
}: InitialsAvatarProps) {
	if (src) {
		return (
			<img
				src={src}
				alt={name}
				className={cn(
					'shrink-0 border border-line-1 object-cover',
					shape === 'round' ? 'rounded-full' : 'rounded-[var(--r-2)]',
					className
				)}
				style={{ width: size, height: size }}
			/>
		);
	}

	const initials = name
		.split(' ')
		.map((w) => w[0])
		.filter(Boolean)
		.slice(0, maxInitials)
		.join('')
		.toUpperCase();

	return (
		<div
			className={cn(
				'flex shrink-0 items-center justify-center border border-line-1 bg-surface-fill font-mono font-medium tracking-[0.04em] text-text-2',
				shape === 'round' ? 'rounded-full' : 'rounded-[var(--r-2)]',
				className
			)}
			style={{
				width: size,
				height: size,
				fontSize: size * 0.35,
			}}
		>
			{initials}
		</div>
	);
}
